'use client';

import { useEffect, useState } from 'react';

// Navigator 확장 인터페이스 정의
interface NavigatorUAData {
  userAgentData?: {
    mobile?: boolean;
  };
  maxTouchPoints?: number;
}

/**
 * UserAgent를 통해 모바일 여부를 판단하는 훅
 * - SSR 안전 처리: navigator 유무 체크
 * - 초기 렌더에서 한 번만 UA 검사
 * - 타입 안전성 강화 및 성능 최적화
 */
export const useUserAgentMobile = () => {
  // 서버에서는 undefined, 클라이언트에서만 계산
  const [isMobile, setIsMobile] = useState<boolean | undefined>(
    typeof window === 'undefined' ? undefined : false,
  );

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const nav = navigator as Navigator & NavigatorUAData;

    // 1) User-Agent Client Hints 우선 사용 (지원 브라우저)
    //    - 데스크톱에서 false가 안정적으로 나오는 값을 우선 신뢰
    const uaDataMobile = nav.userAgentData?.mobile;
    if (typeof uaDataMobile === 'boolean') {
      setIsMobile(uaDataMobile);

      return;
    }

    // 2) iPadOS 13+ 보정: 일부 iPad는 UA에 "Macintosh"가 포함되므로
    //    터치 포인트로 태블릿(iPad) 여부를 보정
    const ua = navigator.userAgent;
    const isProbablyIPad =
      /iPad|Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1;
    if (isProbablyIPad) {
      setIsMobile(true);

      return;
    }

    // 3) 일반 UA 정규식 (태블릿 포함 모바일로 취급)
    //    대소문자 구분 없는 정규식으로 성능 최적화
    const mobileRegex =
      /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile|ipad/i;
    setIsMobile(mobileRegex.test(ua));
  }, []);

  // undefined인 경우 false로 반환 (SSR 호환성)
  return isMobile ?? false;
};
